import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, ShoppingBag, Mail, Calendar, MessageSquare, 
  Settings, LogOut, Menu, X, Bell, Search, ChevronDown, TrendingUp,
  DollarSign, UserPlus, Package, Clock, CheckCircle, AlertCircle,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Eye, Edit, Trash2,
  Filter, Download, RefreshCw, Send, Star, FileText
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin credentials (in production, this should be server-side)
const ADMIN_CREDENTIALS = {
  email: 'admin@clouditllc.ai',
  password: 'CloudIT@Admin2024'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalLeads: 0,
    pendingOrders: 0,
    todayOrders: 0
  });
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [tickets, setTickets] = useState([]);
  
  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');

  useEffect(() => {
    const adminAuth = localStorage.getItem('admin_authenticated');
    if (adminAuth === 'true') {
      setIsAuthenticated(true);
      fetchAllData();
    }
    setLoading(false);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.email === ADMIN_CREDENTIALS.email && loginForm.password === ADMIN_CREDENTIALS.password) {
      setIsAuthenticated(true);
      localStorage.setItem('admin_authenticated', 'true');
      setLoginError('');
      fetchAllData();
    } else {
      setLoginError('Invalid admin credentials');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('admin_authenticated');
    navigate('/admin');
  };

  const fetchAllData = async () => {
    try {
      const [usersRes, ordersRes, leadsRes, consultationsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/admin/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/leads`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/consultations`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/tickets`).catch(() => ({ data: [] }))
      ]);

      setUsers(usersRes.data || []);
      setOrders(ordersRes.data || []);
      setLeads(leadsRes.data || []);
      setConsultations(consultationsRes.data || []);
      setTickets(ticketsRes.data || []);

      // Calculate stats
      const totalRevenue = (ordersRes.data || []).reduce((sum, order) => sum + (order.total || 0), 0);
      const pendingOrders = (ordersRes.data || []).filter(o => o.status === 'pending').length;
      const today = new Date().toDateString();
      const todayOrders = (ordersRes.data || []).filter(o => new Date(o.created_at).toDateString() === today).length;

      setStats({
        totalUsers: (usersRes.data || []).length,
        totalOrders: (ordersRes.data || []).length,
        totalRevenue,
        totalLeads: (leadsRes.data || []).length,
        pendingOrders,
        todayOrders
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const sendEmailToLead = async (lead) => {
    try {
      await axios.post(`${API}/admin/send-email`, {
        to_email: lead.email,
        subject: 'Follow up from CLOUD IT',
        message: `Hi ${lead.name}, thank you for your interest in our services!`
      });
      toast.success('Email sent successfully');
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0A0A0F 0%, #1a1a2e 100%)' }}>
        <div className="w-full max-w-md">
          <div className="bg-[#12121A] border border-white/10 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">
                CLOUD <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">IT</span>
              </h1>
              <p className="text-gray-400">Admin Dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {loginError}
                </div>
              )}

              <div>
                <label className="block text-gray-400 text-sm mb-2">Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6]"
                  placeholder="admin@clouditllc.ai"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#3B82F6]"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 text-white font-semibold rounded-xl bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 transition-opacity"
              >
                Sign In
              </button>
            </form>

            <p className="text-gray-500 text-xs text-center mt-6">
              Protected admin area. Unauthorized access is prohibited.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, badge: stats.pendingOrders },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'leads', label: 'Leads', icon: UserPlus, badge: stats.totalLeads },
    { id: 'consultations', label: 'Consultations', icon: Calendar },
    { id: 'tickets', label: 'Support Tickets', icon: MessageSquare },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#0A0A0F' }}>
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#12121A] border-r border-white/10 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <h1 className="text-2xl font-bold text-white">
              CLOUD <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">IT</span>
            </h1>
            <p className="text-gray-500 text-sm">Admin Panel</p>
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                    activeTab === item.id
                      ? 'bg-gradient-to-r from-[#3B82F6]/20 to-[#8B5CF6]/20 text-white border border-[#3B82F6]/30'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge > 0 && (
                    <span className="px-2 py-0.5 text-xs font-bold bg-[#F97316] text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-[#0A0A0F]/80 backdrop-blur-xl border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 text-gray-400 hover:text-white"
              >
                <Menu size={24} />
              </button>
              <h2 className="text-xl font-semibold text-white capitalize">{activeTab}</h2>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={fetchAllData}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Refresh Data"
              >
                <RefreshCw size={20} />
              </button>
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center text-white font-bold">
                A
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Total Revenue"
                  value={`$${stats.totalRevenue.toLocaleString()}`}
                  icon={DollarSign}
                  trend="+12%"
                  trendUp={true}
                  color="#10B981"
                />
                <StatCard
                  title="Total Orders"
                  value={stats.totalOrders}
                  icon={ShoppingBag}
                  trend="+8%"
                  trendUp={true}
                  color="#3B82F6"
                />
                <StatCard
                  title="Total Users"
                  value={stats.totalUsers}
                  icon={Users}
                  trend="+5%"
                  trendUp={true}
                  color="#8B5CF6"
                />
                <StatCard
                  title="New Leads"
                  value={stats.totalLeads}
                  icon={UserPlus}
                  trend="+15%"
                  trendUp={true}
                  color="#F97316"
                />
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Orders */}
                <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Recent Orders</h3>
                    <button
                      onClick={() => setActiveTab('orders')}
                      className="text-[#3B82F6] text-sm hover:underline"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-3">
                    {orders.slice(0, 5).map((order, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                        <div>
                          <p className="text-white font-medium">{order.customer_name || 'Customer'}</p>
                          <p className="text-gray-500 text-sm">{order.order_number || `#${idx + 1}`}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[#10B981] font-semibold">${(order.total || 0).toFixed(2)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            order.status === 'completed' ? 'bg-[#10B981]/20 text-[#10B981]' :
                            order.status === 'pending' ? 'bg-[#F97316]/20 text-[#F97316]' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {order.status || 'pending'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {orders.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No orders yet</p>
                    )}
                  </div>
                </div>

                {/* Recent Leads */}
                <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Recent Leads</h3>
                    <button
                      onClick={() => setActiveTab('leads')}
                      className="text-[#3B82F6] text-sm hover:underline"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-3">
                    {leads.slice(0, 5).map((lead, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F97316] to-[#EC4899] flex items-center justify-center text-white font-bold">
                            {(lead.name || 'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium">{lead.name}</p>
                            <p className="text-gray-500 text-sm">{lead.email}</p>
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 bg-[#3B82F6]/20 text-[#3B82F6] rounded-full">
                          {lead.lead_type || 'Website'}
                        </span>
                      </div>
                    ))}
                    {leads.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No leads captured yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">All Orders ({orders.length})</h3>
                <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-xl hover:bg-[#3B82F6]/80">
                  <Download size={18} />
                  Export
                </button>
              </div>

              <div className="bg-[#12121A] border border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Order</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Customer</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Items</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Total</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Status</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {orders.map((order, idx) => (
                      <tr key={idx} className="hover:bg-white/5">
                        <td className="px-6 py-4">
                          <p className="text-white font-medium">{order.order_number || `#${idx + 1}`}</p>
                          <p className="text-gray-500 text-sm">{new Date(order.created_at || Date.now()).toLocaleDateString()}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-white">{order.customer_name}</p>
                          <p className="text-gray-500 text-sm">{order.customer_email}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-300">
                          {(order.items || []).length} item(s)
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-[#10B981] font-semibold">${(order.total || 0).toFixed(2)}</p>
                          {order.discount > 0 && (
                            <p className="text-gray-500 text-xs">Saved: ${order.discount.toFixed(2)}</p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={order.status || 'pending'}
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-sm font-medium bg-transparent border ${
                              order.status === 'completed' ? 'border-[#10B981] text-[#10B981]' :
                              order.status === 'processing' ? 'border-[#3B82F6] text-[#3B82F6]' :
                              order.status === 'cancelled' ? 'border-red-500 text-red-500' :
                              'border-[#F97316] text-[#F97316]'
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg">
                              <Eye size={18} />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded-lg">
                              <Send size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orders.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No orders yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">All Users ({users.length})</h3>
              </div>

              <div className="bg-[#12121A] border border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">User</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Email</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Type</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Joined</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {users.map((user, idx) => (
                      <tr key={idx} className="hover:bg-white/5">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center text-white font-bold">
                              {(user.name || 'U')[0].toUpperCase()}
                            </div>
                            <p className="text-white font-medium">{user.name}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-300">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.account_type === 'business' ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 'bg-[#3B82F6]/20 text-[#3B82F6]'
                          }`}>
                            {user.account_type || 'personal'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-400">
                          {new Date(user.created_at || Date.now()).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            user.is_verified ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#F97316]/20 text-[#F97316]'
                          }`}>
                            {user.is_verified ? 'Verified' : 'Pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No users registered yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Captured Leads ({leads.length})</h3>
                <button className="flex items-center gap-2 px-4 py-2 bg-[#10B981] text-white rounded-xl hover:bg-[#10B981]/80">
                  <Download size={18} />
                  Export CSV
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {leads.map((lead, idx) => (
                  <div key={idx} className="bg-[#12121A] border border-white/10 rounded-2xl p-6 hover:border-[#3B82F6]/50 transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F97316] to-[#EC4899] flex items-center justify-center text-white font-bold text-lg">
                          {(lead.name || 'U')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-semibold">{lead.name}</p>
                          <p className="text-gray-500 text-sm">{lead.email}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        lead.status === 'contacted' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' :
                        lead.status === 'converted' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        'bg-[#F97316]/20 text-[#F97316]'
                      }`}>
                        {lead.status || 'new'}
                      </span>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <FileText size={14} />
                        <span>{lead.lead_type || 'Website Audit Guide'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm">
                        <Clock size={14} />
                        <span>{new Date(lead.created_at || Date.now()).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => sendEmailToLead(lead)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#3B82F6]/20 text-[#3B82F6] rounded-lg hover:bg-[#3B82F6]/30 transition-colors"
                      >
                        <Send size={16} />
                        Follow Up
                      </button>
                      <button className="px-3 py-2 bg-white/5 text-gray-400 rounded-lg hover:bg-white/10 transition-colors">
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {leads.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    No leads captured yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Consultations Tab */}
          {activeTab === 'consultations' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Consultation Bookings ({consultations.length})</h3>
              </div>

              <div className="bg-[#12121A] border border-white/10 rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Client</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Type</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Date & Time</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Service</th>
                      <th className="px-6 py-4 text-left text-gray-400 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {consultations.map((consultation, idx) => (
                      <tr key={idx} className="hover:bg-white/5">
                        <td className="px-6 py-4">
                          <p className="text-white font-medium">{consultation.name}</p>
                          <p className="text-gray-500 text-sm">{consultation.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            consultation.consultation_type === 'strategy' ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 'bg-[#10B981]/20 text-[#10B981]'
                          }`}>
                            {consultation.consultation_name || 'Discovery Call'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-white">{consultation.date}</p>
                          <p className="text-gray-500 text-sm">{consultation.time}</p>
                        </td>
                        <td className="px-6 py-4 text-gray-300">{consultation.service || 'General'}</td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs bg-[#3B82F6]/20 text-[#3B82F6] rounded-full">
                            Scheduled
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {consultations.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No consultations booked yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Support Tickets ({tickets.length})</h3>
              </div>

              <div className="space-y-4">
                {tickets.map((ticket, idx) => (
                  <div key={idx} className="bg-[#12121A] border border-white/10 rounded-2xl p-6 hover:border-[#3B82F6]/50 transition-colors">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-white font-semibold text-lg">{ticket.subject || 'Support Request'}</h4>
                        <p className="text-gray-500 text-sm">From: {ticket.name} ({ticket.email})</p>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full ${
                        ticket.status === 'resolved' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        ticket.status === 'in_progress' ? 'bg-[#3B82F6]/20 text-[#3B82F6]' :
                        'bg-[#F97316]/20 text-[#F97316]'
                      }`}>
                        {ticket.status || 'Open'}
                      </span>
                    </div>
                    <p className="text-gray-300 mb-4">{ticket.message}</p>
                    <div className="flex items-center gap-4">
                      <button className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg hover:bg-[#3B82F6]/80">
                        <Send size={16} />
                        Reply
                      </button>
                      <button className="flex items-center gap-2 px-4 py-2 bg-[#10B981]/20 text-[#10B981] rounded-lg hover:bg-[#10B981]/30">
                        <CheckCircle size={16} />
                        Mark Resolved
                      </button>
                    </div>
                  </div>
                ))}
                {tickets.length === 0 && (
                  <div className="text-center py-12 text-gray-500 bg-[#12121A] border border-white/10 rounded-2xl">
                    No support tickets yet
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl space-y-6">
              <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Admin Credentials</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Admin Email</label>
                    <input
                      type="email"
                      value="admin@clouditllc.ai"
                      disabled
                      className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Password</label>
                    <input
                      type="password"
                      value="••••••••••••"
                      disabled
                      className="w-full bg-[#0A0A0F] border border-white/10 rounded-xl px-4 py-3 text-gray-500"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Integrations Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <span className="text-gray-300">PayPal</span>
                    <span className="px-2 py-1 text-xs bg-[#10B981]/20 text-[#10B981] rounded-full">Connected</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <span className="text-gray-300">Email (Gmail SMTP)</span>
                    <span className="px-2 py-1 text-xs bg-[#F97316]/20 text-[#F97316] rounded-full">Needs App Password</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                    <span className="text-gray-300">AI Chatbot (GPT-5.2)</span>
                    <span className="px-2 py-1 text-xs bg-[#10B981]/20 text-[#10B981] rounded-full">Active</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendUp, color }) => (
  <div className="bg-[#12121A] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors">
    <div className="flex items-start justify-between mb-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}20` }}>
        <Icon size={24} style={{ color }} />
      </div>
      <div className={`flex items-center gap-1 text-sm ${trendUp ? 'text-[#10B981]' : 'text-red-400'}`}>
        {trendUp ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
        {trend}
      </div>
    </div>
    <p className="text-gray-400 text-sm mb-1">{title}</p>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

export default AdminDashboard;
